import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useMemo, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo } from '@/componentes/Campo';
import { CoMarcaLogin } from '@/componentes/Marca';
import { OlhoDaSenha } from '@/componentes/OlhoDaSenha';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { primeiraMaiuscula } from '@/lib/texto';
import { codigoDoFragmento } from '@/modulos/cadastros/convite';

import { api } from './api';
import { AJUDA_DA_SENHA, problemaObvioNaSenha } from '@/lib/senha';

/**
 * O primeiro acesso de um ADMINISTRADOR DE PLATAFORMA (migração 0029): o
 * convite sai do script de quem opera o servidor, e a pessoa escolhe a senha
 * aqui. O código vem do fragmento do endereço, como no convite de empresa.
 */
/* A mensagem vem de `problemaObvioNaSenha`, num lugar so: o numero estava
   escrito a mao em quatro telas, com quatro frases diferentes. */
const SENHAS_DIFERENTES = 'As duas senhas não são iguais. Digite a mesma senha nos dois campos.';

/* As mesmas duas regras de antes, agora conferidas AO SAIR de cada campo e
   escritas embaixo dele (p.16; R01, p.28). O envio confere de novo, como rede,
   e leva o foco ao primeiro campo com problema. */
/* A MESMA regra da empresa, e de propósito: o administrador de plataforma é
   mais privilegiado que qualquer usuário de empresa, e exigir menos dele faria
   a porta mais larga do sistema ser a menos guardada. */
const problemaDaSenha = problemaObvioNaSenha;
const problemaDaRepeticao = (senha: string, repeticao: string) => (senha !== repeticao ? SENHAS_DIFERENTES : null);

export function PaginaPrimeiroAcessoPlataforma() {
  const base = useId();
  const codigo = useMemo(() => codigoDoFragmento(window.location.hash), []);
  const [senha, setSenha] = useState('');
  const [repeticao, setRepeticao] = useState('');
  /* OS DOIS OLHOS (26/10/2026). Esta tela monta o `input` à mão — ela é a
     única da mesa que não passa pelo `CampoTexto`, porque o primeiro acesso da
     plataforma valida os dois campos um contra o outro. O botão é o MESMO
     componente compartilhado, então o rótulo, o ícone e o estado não têm como
     divergir do resto do sistema; o que se repete aqui é só o `useState`. */
  const [verSenha, setVerSenha] = useState(false);
  const [verRepeticao, setVerRepeticao] = useState(false);
  const [erroDaSenha, setErroDaSenha] = useState<string | null>(null);
  const [erroDaRepeticao, setErroDaRepeticao] = useState<string | null>(null);
  const aceitar = useMutation({
    mutationFn: () => api.aceitarConvite(codigo ?? '', senha),
    meta: {
      retorno: retornoDe<{ email: string }>((r) => ({
        titulo: 'Senha de administração criada',
        alvo: { nome: r.email },
        efeito: 'Entre na plataforma com o e-mail e a senha nova',
      })),
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const daSenha = problemaDaSenha(senha);
    const daRepeticao = daSenha ? null : problemaDaRepeticao(senha, repeticao);
    setErroDaSenha(daSenha);
    setErroDaRepeticao(daRepeticao);
    if (daSenha) {
      document.getElementById(`${base}-senha`)?.focus();
      return;
    }
    if (daRepeticao) {
      document.getElementById(`${base}-repeticao`)?.focus();
      return;
    }
    aceitar.mutate();
  }

  const recusa = aceitar.isError ? explicarFalha(aceitar.error) : null;

  return (
    <div className="h-full overflow-auto bg-tela">
      <div className="mx-auto flex max-w-[688px] flex-col gap-8 p-12">
        <CoMarcaLogin />
        <CabecalhoPagina sobretitulo="Administração da plataforma" titulo="Primeiro acesso" subtitulo="Escolha a sua senha de administrador de plataforma" />
        <section className="cartao flex flex-col gap-4 p-6">
          {codigo === null ? (
            <div role="alert" className="flex flex-col gap-1">
              <span className="txt-corpo text-tinta">O endereço do convite está incompleto.</span>
              <span className="txt-apoio text-tinta-secundaria">Confira se o endereço foi copiado inteiro, com a parte depois do #, ou peça outro convite a quem opera o servidor.</span>
            </div>
          ) : aceitar.isSuccess ? (
            <>
              <p className="txt-corpo text-tinta">{`Senha gravada. Entre com ${aceitar.data.email}.`}</p>
              <div className="flex justify-end">
                <Link to="/plataforma" className="botao botao-primario">
                  Ir para a administração
                </Link>
              </div>
            </>
          ) : (
            <form noValidate className="flex flex-col gap-4" onSubmit={enviar}>
              <Campo id={`${base}-senha`} rotulo="Senha" ajuda={AJUDA_DA_SENHA} erro={erroDaSenha}>
                <div className="relative">
                <input
                  id={`${base}-senha`}
                  type={verSenha ? 'text' : 'password'}
                  autoComplete="new-password"
                  maxLength={200}
                  className="controle w-full pr-10"
                  value={senha}
                  onChange={(e) => {
                    setErroDaSenha(null);
                    setSenha(e.target.value);
                  }}
                  onBlur={(e) => {
                    setErroDaSenha(problemaDaSenha(e.target.value));
                  }}
                  {...(erroDaSenha ? { 'aria-invalid': true, 'aria-describedby': `${base}-senha-erro` } : {})}
                />
                <OlhoDaSenha
                  visivel={verSenha}
                  aoAlternar={() => {
                    setVerSenha((v) => !v);
                  }}
                />
                </div>
              </Campo>
              <Campo id={`${base}-repeticao`} rotulo="Repita a senha" erro={erroDaRepeticao}>
                <div className="relative">
                <input
                  id={`${base}-repeticao`}
                  type={verRepeticao ? 'text' : 'password'}
                  autoComplete="new-password"
                  maxLength={200}
                  className="controle w-full pr-10"
                  value={repeticao}
                  onChange={(e) => {
                    setErroDaRepeticao(null);
                    setRepeticao(e.target.value);
                  }}
                  onBlur={(e) => {
                    /* So aponta a diferenca depois que ha o que comparar. */
                    setErroDaRepeticao(e.target.value === '' ? null : problemaDaRepeticao(senha, e.target.value));
                  }}
                  {...(erroDaRepeticao ? { 'aria-invalid': true, 'aria-describedby': `${base}-repeticao-erro` } : {})}
                />
                <OlhoDaSenha
                  visivel={verRepeticao}
                  aoAlternar={() => {
                    setVerRepeticao((v) => !v);
                  }}
                />
                </div>
              </Campo>
              {recusa ? (
                <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
                  <span className="txt-corpo text-tinta">{primeiraMaiuscula(recusa.erro)}</span>
                  <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
                </div>
              ) : null}
              <div className="flex justify-end">
                <Botao nivel="primario" type="submit" disabled={aceitar.isPending}>
                  {aceitar.isPending ? 'Gravando…' : 'Definir senha'}
                </Botao>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
